"use client";

import { useCallback, useEffect, useMemo, useState } from "react";

import * as authApi from "./api";
import { AUTH_PERSIST_KEY, useAuthStore } from "./store";
import type { AuthUser, SessionImpersonation } from "./types";

/** Wait until zustand `persist` has rehydrated from localStorage (client only). */
export function useAuthStoreHydrated(): boolean {
  const [hydrated, setHydrated] = useState(() => useAuthStore.persist.hasHydrated());

  useEffect(() => {
    const unsub = useAuthStore.persist.onFinishHydration(() => setHydrated(true));
    return unsub;
  }, []);

  return hydrated;
}

export function useAuth() {
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.token);
  const tenantId = useAuthStore((s) => s.tenantId);
  const role = useAuthStore((s) => s.role);
  const impersonation = useAuthStore((s) => s.impersonation);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.storageArea !== window.localStorage || event.key !== AUTH_PERSIST_KEY) {
        return;
      }

      if (!event.newValue) {
        useAuthStore.getState().clearSession();
        return;
      }

      try {
        const parsed = JSON.parse(event.newValue) as { state?: { user?: unknown; token?: unknown; tenantId?: unknown; role?: unknown; impersonation?: unknown } };
        const nextState = parsed?.state;
        if (!nextState || !nextState.user) {
          useAuthStore.getState().clearSession();
          return;
        }
        useAuthStore.setState({
          user: nextState.user as AuthUser,
          token: typeof nextState.token === "string" ? nextState.token : null,
          tenantId: typeof nextState.tenantId === "string" ? nextState.tenantId : null,
          role: typeof nextState.role === "string" ? nextState.role : ((nextState.user as AuthUser | null)?.role ?? null),
          impersonation: (nextState.impersonation as SessionImpersonation | null | undefined) ?? null,
        });
      } catch {
        useAuthStore.getState().clearSession();
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    await authApi.loginAndPersist({ email, password });
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout();
  }, []);

  const getCurrentUser = useCallback(async (signal?: AbortSignal): Promise<AuthUser | null> => {
    return authApi.getCurrentUser(signal);
  }, []);

  const refreshSession = useCallback(async () => {
    return authApi.refresh();
  }, []);

  const exitImpersonation = useCallback(async () => {
    return authApi.exitImpersonation();
  }, []);

  const isAuthenticated = Boolean(user);

  return useMemo(
    () => ({
      user,
      token,
      tenantId,
      role,
      impersonation,
      isAuthenticated,
      login,
      logout,
      getCurrentUser,
      refreshSession,
      exitImpersonation,
    }),
    [user, token, tenantId, role, impersonation, isAuthenticated, login, logout, getCurrentUser, refreshSession, exitImpersonation],
  );
}
